'use client';

import styles from './QuestionnaireFirmAside.module.css';
import {
  Card,
  Flex,
  Pill,
  Heading,
  Text,
  QuestionnaireAsideTheme,
  Button,
  Avatar,
} from '@/_components';
import { useContext, useMemo, type FC } from 'react';
import type { QuestionnaireFirmAsideProps } from './QuestionnaireFirmAside.types';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { useRouter } from 'next/navigation';
import { getClasses } from '@/_utils/classnames';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import type { FirmUserAsAdvisor } from '@/_types/models/user';

export const QuestionnaireFirmAside: FC<QuestionnaireFirmAsideProps> = ({
  isPreview,
}) => {
  const { advisor, isUserReadOnly, isCompanyReadOnly } =
    useContext(UserContext);
  const { previewAdvisor } = useContext(UserPreviewContext);
  const { preferences, firm } = useContext(FirmSettingsContext);
  const { push } = useRouter();

  const hasCustomTheme: boolean = !!preferences?.theme?.primary;
  const hasCustomElements: boolean =
    !!preferences?.custom_element_enabled &&
    (!!firm?.custom_elements?.length || !!preferences?.customElements?.length);
  const isReadOnly: boolean = isUserReadOnly || isCompanyReadOnly;
  const shouldShowActionButtons =
    hasCustomElements || !preferences.communication_disabled;

  const memoAdvisor: FirmUserAsAdvisor | undefined = useMemo(
    () => (isPreview ? previewAdvisor : advisor),
    [isPreview, previewAdvisor, advisor],
  );

  // TODO: Remove when all moved to React, this is to accommodate the old custom elements in firm preferences
  const htmlCustomElements: Array<{ link: string; text: string }> | undefined =
    useMemo(() => {
      if (!preferences?.customElements) return undefined;

      try {
        // Create a temporary container to safely parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          `<div>${preferences.customElements}</div>`,
          'text/html',
        );

        // Extract all anchor elements
        const anchorElements = doc.querySelectorAll('a');

        return Array.from(anchorElements)
          .map((anchor) => {
            const link = anchor.getAttribute('href') || '';
            const text = anchor.textContent?.trim() || '';

            // Only return elements with both link and text
            return link && text ? { link, text } : null;
          })
          .filter(
            (item): item is { link: string; text: string } => item !== null,
          );
      } catch (error) {
        console.warn('Failed to parse custom elements HTML:', error);
        return undefined;
      }
    }, [preferences?.customElements]);

  const uniqueCustomElements: Array<{ link: string; text: string }> = useMemo(
    () =>
      [...(htmlCustomElements ?? []), ...(firm?.custom_elements ?? [])].filter(
        (element, index, self) =>
          index ===
          self.findIndex(
            (t) => t.link === element.link && t.text === element.text,
          ),
      ),
    [htmlCustomElements, firm?.custom_elements],
  );

  if (!memoAdvisor?.id) return null;

  const questionnaireFirmAsideClasses: string = getClasses(
    styles.questionnaireFirmAside,
    {
      [styles.questionnaireFirmAsidePadding]: hasCustomTheme,
    },
  );

  const onMessageClick = () => push('/inbox');

  return (
    <Card
      padding={8}
      isColumn={true}
      gap={12}
      className={questionnaireFirmAsideClasses}
    >
      {hasCustomTheme && <QuestionnaireAsideTheme />}
      <Flex isColumn={true} gap={4}>
        {memoAdvisor.firmName && (
          <Flex>
            <Pill
              label={memoAdvisor.firmName}
              intent="default"
              isRound={true}
            />
          </Flex>
        )}
        <Flex isYCentered={true} gap={4}>
          <Avatar
            name={memoAdvisor?.name}
            avatarUrl={memoAdvisor?.avatar}
            size={32}
            type="firm"
          />
          <Heading level={5}>{memoAdvisor?.name}</Heading>
        </Flex>
        {!preferences.communication_disabled && (
          <Text type="small" color="secondary">
            Have a question? Please feel free to send us a message.
          </Text>
        )}
      </Flex>
      {shouldShowActionButtons && (
        <Flex isColumn={true} gap={4} isFullWidth={true}>
          {!preferences.communication_disabled && (
            <Button
              intent="primary"
              value="Send a message"
              isDisabled={isReadOnly}
              size="small"
              onClick={onMessageClick}
              iconLeft="send"
            />
          )}
          {hasCustomElements &&
            uniqueCustomElements.map((element) => (
              <div
                key={`${element.link}-${element.text}`}
                className={styles.customElementsButton}
              >
                <div className={styles.buttonWrapper}>
                  <Button
                    intent="secondary"
                    size="small"
                    value={element.text}
                    isExpandable={true}
                    onClick={() => window.open(element.link, '_blank')}
                  />
                </div>
              </div>
            ))}
        </Flex>
      )}
    </Card>
  );
};
